import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { prepareQueryInput } from '#domain/performance/kpi/prepare-query-input';
import { getLikeForLikePerimeter } from '#domain/performance/likeForLike/likeForLike.query';
import { ApiErrorCode, LIKE_FOR_LIKE_PERIMETER_FILTER_KEYS } from '@carrefour-gcs/shared';
import { useQueryClient } from '@tanstack/vue-query';
import { computed } from 'vue';

const PERIMETER_FILTER_KEYS: ReadonlyArray<string> = LIKE_FOR_LIKE_PERIMETER_FILTER_KEYS;

/**
 * Resolves the L4L store perimeter for KPI query functions.
 *
 * The perimeter goes through the query cache (ensureQueryData): every KPI request awaiting it
 * shares a single fetch per perimeter-relevant key, and the KPI queries stay in their native
 * fetching state while it resolves. Returning undefined hands the resolution back to the API
 * (non-L4L mode, or the perimeter route failed).
 */
export function useLikeForLikePerimeter() {
  const queryClient = useQueryClient();

  const {
    withTax: { isWithTax },
    taxonomy: { taxonomy },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod, comparisonPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded }
  } = useKpisPerimeter();

  // Key restricted to perimeter-invalidating inputs: a product filter change must not refetch.
  const perimeterRelevantFilters = computed(() =>
    Object.fromEntries(
      Object.entries(selectedFiltersKpiQuery.value ?? {}).filter(([key]) => PERIMETER_FILTER_KEYS.includes(key))
    )
  );

  async function resolveLikeForLikeStoreKeys(): Promise<string[] | undefined> {
    if (storePerimeter.value !== 'likeForLike') {
      return undefined;
    }

    try {
      const perimeter = await queryClient.ensureQueryData({
        queryKey: [getLikeForLikePerimeter.name, selectedPeriod.value, perimeterRelevantFilters.value],
        queryFn: async ({ signal }) => {
          const input = prepareQueryInput({
            period: selectedPeriod,
            comparisonPeriod,
            filters: selectedFiltersKpiQuery,
            isWithTax,
            taxonomy,
            storePerimeter,
            isMyDataIncluded
          });

          const result = await getLikeForLikePerimeter(input, signal);

          if (result.status === 'success') {
            return result.data;
          }
          if (result.error === ApiErrorCode.UNAUTHORIZED_DATA_ACCESS_PERMISSIONS) {
            return null; // not eligible to L4L: no keys, the API enforces its own checks
          }
          throw new Error(result.error);
        }
      });

      return perimeter?.eligibleStoreKeys;
    } catch {
      return undefined; // the API resolves the perimeter itself
    }
  }

  return { resolveLikeForLikeStoreKeys };
}
